import React from 'react'
import { Logger } from 'wdc-cube'

import { makeStyles } from 'tss-react/mui'

import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import LinearProgress from '@mui/material/LinearProgress'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid'

import { ManageTypesScope } from 'src/modules/admin/configurations/scopes'
import {
    buttonSx,
    chipSx,
    classToFComponent,
    dataGridRowHoverSx,
    dataGridSx,
    FCClassContext,
    Field,
    FieldHelp,
    FieldLabel,
    inputSx,
    LifesHubConfirmDialog,
    LifesHubEmptyState,
    ModalClose,
    ModalFooter,
    ModalFooterActions,
    PanelCard,
    SwitchRow,
    switchSx
} from 'src/utils/views'

import { NotificationType } from '../../user-manager/um_scopes'
import { SettingsPanel } from 'src/utils/views/modules/admin-configurations'

const LOG = Logger.get('ManageTypesModal')

const useStyles = makeStyles()({
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: '24px 26px 20px',
        minHeight: 0
    },
    head: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 18,
        alignItems: 'flex-start',
        '& h2': { margin: '0 0 5px', fontSize: 20, lineHeight: 1.2, color: 'var(--ink)' },
        '& p': { margin: 0, color: 'var(--muted)', fontSize: 13 }
    },
    form: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto auto auto',
        gap: 12,
        alignItems: 'end',
        '@media (max-width: 720px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            '& .MuiButton-root': { width: '100%' }
        }
    },
    input: {
        ...inputSx
    },
    switchField: {
        display: 'flex',
        alignItems: 'center',
        minHeight: 43
    },
    tableCard: {
        minHeight: 320
    },
    cardBody: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flexGrow: 1,
        padding: 18
    },
    grid: {
        ...dataGridSx,
        ...dataGridRowHoverSx,
        minHeight: 260,
        '& .MuiDataGrid-cell.actions': {
            padding: '0 6px'
        }
    }
})

export type ManageTypesModalProps = {
    scope: ManageTypesScope
}

class ManageTypesModalClass implements FCClassContext<ManageTypesModalProps> {
    readonly onClose = () => this.scope.onClose().catch(LOG.caught)

    readonly onSaveType = () => this.scope.onSave().catch(LOG.caught)

    readonly onNewClick = () => () => {
        this.scope.onNew().catch(LOG.caught)
    }

    readonly emitEditClick = (id: GridRowId) => () => {
        this.scope.onEdit(Number(id)).catch(LOG.caught)
    }

    readonly emitDeleteClick = (id: GridRowId) => () => {
        this.scope.onDelete(Number(id)).catch(LOG.caught)
    }

    readonly emitNameChange = (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        this.scope.onNameChange(evt.target.value).catch(LOG.caught)

    readonly emitActiveChange = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onActiveChange(evt.target.checked).catch(LOG.caught)

    readonly emitOnYesResponse = () => {
        this.scope.onYesNoResponse(true).catch(LOG.caught)
    }

    readonly emitOnNoResponse = () => {
        this.scope.onYesNoResponse(false).catch(LOG.caught)
    }

    scope!: ManageTypesScope
    columns = static_build_columns(this.emitEditClick, this.emitDeleteClick)

    onSyncState({ scope }: ManageTypesModalProps) {
        this.scope = scope
    }

    render() {
        const { classes } = useStyles()

        const scope = this.scope
        const types = scope.allNotificationTypes
        const isNew = !scope.typeId

        return (
            <Dialog
                open={scope.manageTypesOpen}
                onClose={this.onClose}
                maxWidth={false}
                slotProps={{
                    paper: {
                        sx: {
                            width: 'min(760px, calc(100vw - 28px))',
                            borderRadius: 'var(--radius)',
                            boxShadow: 'var(--shadow-2)',
                            backgroundImage: 'none'
                        }
                    }
                }}
            >
                {!!scope.waiting && (
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: 'var(--green-700)' } }} />
                )}

                <div className={classes.content}>
                    <div className={classes.head}>
                        <div>
                            <h2>Tipos de notificação</h2>
                            <p>Agrupam as notificações para o usuário filtrar no sino.</p>
                        </div>
                        <ModalClose aria-label="Fechar" onClick={this.onClose}>
                            <CloseIcon sx={{ fontSize: 20 }} />
                        </ModalClose>
                    </div>

                    <SettingsPanel>
                        <div className={classes.form}>
                            <Field>
                                <FieldLabel htmlFor="notificationTypeName">
                                    {isNew ? 'Novo tipo' : 'Editando tipo'}
                                </FieldLabel>
                                <TextField
                                    id="notificationTypeName"
                                    className={classes.input}
                                    fullWidth
                                    value={scope.name}
                                    onChange={this.emitNameChange}
                                    placeholder="Nome do tipo"
                                />
                            </Field>
                            <div className={classes.switchField}>
                                <SwitchRow>
                                    <Switch
                                        sx={switchSx}
                                        size="small"
                                        checked={scope.active}
                                        onChange={this.emitActiveChange}
                                        slotProps={{
                                            input: { 'aria-label': 'Tipo ativo' }
                                        }}
                                    />
                                    <div>
                                        <b>Ativo</b>
                                        <FieldHelp>Só tipos ativos podem ser escolhidos.</FieldHelp>
                                    </div>
                                </SwitchRow>
                            </div>
                            <Button sx={buttonSx()} startIcon={<AddIcon />} onClick={this.onNewClick()}>
                                Limpar
                            </Button>
                            <Button sx={buttonSx('primary')} startIcon={<SaveOutlinedIcon />} onClick={this.onSaveType}>
                                Salvar tipo
                            </Button>
                        </div>
                    </SettingsPanel>

                    <PanelCard elevation={0} className={classes.tableCard}>
                        {types.length === 0 ? (
                            <LifesHubEmptyState
                                title="Nenhum tipo cadastrado"
                                text="Cadastre um tipo no formulário acima para poder classificar as notificações."
                            />
                        ) : (
                            <div className={classes.cardBody}>
                                <DataGrid
                                    className={classes.grid}
                                    rows={types}
                                    columns={this.columns}
                                    getRowId={static_getRowId}
                                    disableRowSelectionOnClick
                                    density="compact"
                                    initialState={{
                                        pagination: {
                                            paginationModel: {
                                                pageSize: 10
                                            }
                                        }
                                    }}
                                    pageSizeOptions={[10, 25, 50]}
                                />
                            </div>
                        )}
                    </PanelCard>

                    <LifesHubConfirmDialog
                        open={!!scope.askYesOrNo}
                        title="Confirmar exclusão"
                        tone="danger"
                        message={scope.askYesOrNo}
                        confirmLabel="Excluir"
                        footerNote="A remoção é imediata e não pode ser desfeita por aqui."
                        onConfirm={this.emitOnYesResponse}
                        onCancel={this.emitOnNoResponse}
                    />
                </div>

                <ModalFooter>
                    <ModalFooterActions>
                        <Button sx={buttonSx()} onClick={this.onClose}>
                            Fechar
                        </Button>
                    </ModalFooterActions>
                </ModalFooter>
            </Dialog>
        )
    }
}

export default classToFComponent(ManageTypesModalClass)

function static_build_columns(
    handleEditClick: (id: GridRowId) => () => void,
    handleDeleteClick: (id: GridRowId) => () => void
): GridColDef[] {
    return [
        {
            field: 'name',
            headerName: 'Tipo',
            flex: 1,
            minWidth: 220,
            renderCell({ value }) {
                const label = (value as string) ?? '—'
                return (
                    <span style={{ fontWeight: 750, overflow: 'hidden', textOverflow: 'ellipsis' }} title={label}>
                        {label}
                    </span>
                )
            }
        },
        {
            field: 'active',
            headerName: 'Status',
            align: 'center',
            headerAlign: 'center',
            width: 120,
            renderCell(params) {
                return params.value ? (
                    <Chip size="small" label="Ativo" sx={chipSx('green')} />
                ) : (
                    <Chip size="small" label="Inativo" sx={chipSx('gray')} />
                )
            }
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 96,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        key={'edit' + id}
                        icon={
                            <Tooltip title="Editar tipo">
                                <EditOutlinedIcon sx={{ fontSize: 20 }} />
                            </Tooltip>
                        }
                        label="Editar tipo"
                        onClick={handleEditClick(id)}
                        style={{ color: 'var(--green-800)' }}
                    />,
                    <GridActionsCellItem
                        key={'del' + id}
                        icon={
                            <Tooltip title="Excluir tipo">
                                <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                            </Tooltip>
                        }
                        label="Excluir tipo"
                        onClick={handleDeleteClick(id)}
                        style={{ color: 'var(--danger)' }}
                    />
                ]
            }
        }
    ]
}

function static_getRowId(row: NotificationType) {
    return `${row.id}`
}
